// App.js
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import {
    Box,
    Flex,
    useToast,
    Spinner,
    Center,
} from '@chakra-ui/react';
import { v4 as uuidv4 } from 'uuid';
import TopNav from './components/TopNav';
import ChatView from './components/ChatView';
import ErrorModal from './components/ErrorModal';
import { useModelOptions, useSystemPromptOptions, useDefaults } from './components/hooks/useOptions';
import { getErrorDisplayInfo, isRetryableError, shouldShowModal } from './utils/errorHandler';
import DocumentLibrary from './components/DocumentLibrary';
import UserManagement from './components/UserManagement';
import WorkspaceManagement from './components/WorkspaceManagement';
import CredentialManagement from './components/CredentialManagement';
import Login from './components/Login';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import { fetchWithAuth } from './utils/fetchWithAuth';
import ChangePasswordModal from './components/ChangePassword';
import { startActivityTracking, stopActivityTracking } from './utils/activityTracker';

// ─── Main App Content (requires authentication) ───────────────────────────────

// `adminWithoutWorkspace` is set by WorkspaceGate for a platform superuser who
// holds no membership. It disables chat (every write names a workspace, and
// there is none to name) while leaving the admin surface fully reachable so the
// grant path stays available. See the long note on WorkspaceGate.
function AppContent({ adminWithoutWorkspace = false, workspaceError = null }) {
    const [model, setModel] = useState('');
    const [systemPrompt, setSystemPrompt] = useState('');
    const [messages, setMessages] = useState([]);
    const [conversations, setConversations] = useState([]);
    const [currentConversationId, setCurrentConversationId] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingConversation, setIsLoadingConversation] = useState(false);
    // An admin with no workspace cannot use chat, so landing them there would
    // show a dead screen. Start them on the surface that can fix it.
    const [currentView, setCurrentView] = useState(
        adminWithoutWorkspace ? 'workspaces' : 'chat'
    );
    const [showChangePassword, setShowChangePassword] = useState(false);

    const [retryState, setRetryState] = useState({
        isRetrying: false,
        pendingRetry: null,
        errorDetail: null,
        errorInfo: null,
    });
    const [showErrorModal, setShowErrorModal] = useState(false);

    // ── Hooks ──────────────────────────────────────────────────────────────────

    const { options: modelOptions } = useModelOptions();
    const { options: systemPromptOptions } = useSystemPromptOptions();
    const { defaults } = useDefaults();
    const toast = useToast();
    const { user, logout } = useAuth();
    // Every write names the workspace explicitly -- the server validates
    // membership and never infers it. See services/workspace_access.py.
    // `workspaces` is needed for the move-conversation destination list: a
    // conversation may only be moved to a workspace the user is a member of,
    // and this list is exactly that (server-provided, membership-scoped).
    const { currentWorkspaceId, workspaces } = useWorkspace();

    // ── Refs ───────────────────────────────────────────────────────────────────

    const currentConversationIdRef = useRef(currentConversationId);
    const conversationsRef = useRef(conversations);
    const modelOptionsRef = useRef(modelOptions);
    const systemPromptOptionsRef = useRef(systemPromptOptions);
    const messageDisplayRef = useRef(null);

    // ── Effects: option defaults & validation ──────────────────────────────────

    useEffect(() => {
        if (defaults) {
            setModel(prev => prev || defaults.model);
            setSystemPrompt(prev => prev || defaults.system_prompt);
        }
    }, [defaults]);

    useEffect(() => {
        if (modelOptions.length > 0 && model) {
            const isValid = modelOptions.some(opt => opt.value === model);
            if (!isValid) {
                const fallback = defaults?.model || modelOptions[0].value;
                console.warn(`Model "${model}" not available, resetting to "${fallback}"`);
                setModel(fallback);
            }
        }
    }, [modelOptions, model, defaults]);

    useEffect(() => {
        if (systemPromptOptions.length > 0 && systemPrompt) {
            const isValid = systemPromptOptions.some(opt => opt.value === systemPrompt);
            if (!isValid) {
                const fallback = defaults?.system_prompt || systemPromptOptions[0].value;
                console.warn(`System prompt "${systemPrompt}" not available, resetting to "${fallback}"`);
                setSystemPrompt(fallback);
            }
        }
    }, [systemPromptOptions, systemPrompt, defaults]);

    useEffect(() => { modelOptionsRef.current = modelOptions; }, [modelOptions]);
    useEffect(() => { systemPromptOptionsRef.current = systemPromptOptions; }, [systemPromptOptions]);

    const currentModelDisplayName = useMemo(() => {
        const option = modelOptions.find(opt => opt.value === model);
        return option ? option.label : model ? model.split('-')[0] : '';
    }, [model, modelOptions]);

    // ── Effects: misc ──────────────────────────────────────────────────────────

    useEffect(() => {
        window.chakraToast = toast;
        return () => { delete window.chakraToast; };
    }, [toast]);

    useEffect(() => {
        if (user) {
            console.info('[App] User authenticated - starting heartbeat');
            startActivityTracking();
            return () => {
                console.info('[App] Component unmounting - stopping heartbeat');
                stopActivityTracking();
            };
        }
    }, [user]);

    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash.slice(1);
            // Chat and documents both need a workspace; for an admin who has
            // none, every route that is not an admin route falls back to the
            // workspace admin screen rather than a view that cannot load.
            if (adminWithoutWorkspace) {
                if (hash === 'users') setCurrentView('users');
                else if (hash === 'credentials') setCurrentView('credentials');
                else setCurrentView('workspaces');
                return;
            }
            if (hash === 'documents') setCurrentView('documents');
            else if (hash === 'users' && user?.is_admin) setCurrentView('users');
            // Admin-only, same guard as 'users': a non-admin landing on
            // #workspaces falls through to chat rather than rendering a screen
            // whose every request would 403.
            else if (hash === 'workspaces' && user?.is_admin) setCurrentView('workspaces');
            // Admin-only, same guard: credentials are provider API keys, and
            // per Steph's option A only a platform admin may manage them.
            else if (hash === 'credentials' && user?.is_admin) setCurrentView('credentials');
            else setCurrentView('chat');
        };
        handleHashChange();
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
        // adminWithoutWorkspace is read inside the handler, so it belongs here:
        // a stale closure would keep routing to chat after a grant lands.
    }, [user, adminWithoutWorkspace]);

    useEffect(() => { currentConversationIdRef.current = currentConversationId; }, [currentConversationId]);
    // Defence in depth (ai-chat#2): handleSubmit reads the workspace from this ref so a
    // stale closure can never send a message to the previous workspace.
    const currentWorkspaceIdRef = useRef(currentWorkspaceId);
    useEffect(() => { currentWorkspaceIdRef.current = currentWorkspaceId; }, [currentWorkspaceId]);
    useEffect(() => { conversationsRef.current = conversations; }, [conversations]);

    useEffect(() => {
        if (error) {
            toast({ title: 'Error', description: error, status: 'error', duration: 5000, isClosable: true });
            setError(null);
        }
    }, [error, toast]);

    useEffect(() => {
        if (retryState.errorInfo?.showModal) setShowErrorModal(true);
    }, [retryState.errorInfo]);

    // ── Conversation management ────────────────────────────────────────────────

    const fetchConversations = useCallback(async () => {
        if (currentWorkspaceId == null) return;
        const requestedWorkspaceId = currentWorkspaceId;
        try {
            // Scoped to the current workspace: the user is in one workspace at a
            // time. Omitting the param would list every workspace they belong
            // to, which is the shape cross-workspace search will use later.
            const response = await fetchWithAuth(
                `/api/conversations?workspace_id=${currentWorkspaceId}`, {}
            );
            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();
            // ai-chat#5: the user may have switched workspace while this
            // request was in flight (slow response, or a delayed refresh from a
            // stream that started in the previous workspace). Drop the answer:
            // appending it would show the old workspace's list in the new one.
            if (currentWorkspaceIdRef.current !== requestedWorkspaceId) return;
            setConversations(prevConversations => {
                const tempConversations = prevConversations.filter(conv => conv.isTemporary);
                return [...tempConversations, ...data];
            });
        } catch (error) {
            console.error('Error fetching conversations:', error);
        }
    }, [currentWorkspaceId]);

    const cleanupTemporaryConversations = useCallback(() => {
        setConversations(prevConversations => {
            const emptyTemporaryConvs = prevConversations.filter(
                conv => conv.isTemporary && conv.name === 'New Conversation' && (!conv.messages || conv.messages.length === 0)
            );
            if (emptyTemporaryConvs.length > 1) {
                const [keepConv] = emptyTemporaryConvs;
                return prevConversations.filter(conv =>
                    !conv.isTemporary ||
                    conv === keepConv ||
                    (conv.isTemporary && conv.messages && conv.messages.length > 0)
                );
            }
            return prevConversations;
        });
    }, []);

    const prevConversationsCountRef = useRef(0);
    useEffect(() => {
        const tempConvCount = conversations.filter(
            c => c.isTemporary && c.name === 'New Conversation' && (!c.messages || c.messages.length === 0)
        ).length;
        if (tempConvCount > 1 && tempConvCount !== prevConversationsCountRef.current) {
            cleanupTemporaryConversations();
        }
        prevConversationsCountRef.current = tempConvCount;
    }, [conversations, cleanupTemporaryConversations]);

    // Switching workspace must not leave the previous workspace's conversation
    // selected: it is no longer readable, and the next message would be written
    // into the wrong place (or rejected). Clear the view, then let the effect
    // below repopulate from the new workspace.
    const previousWorkspaceIdRef = useRef(currentWorkspaceId);
    useEffect(() => {
        if (previousWorkspaceIdRef.current === currentWorkspaceId) return;
        previousWorkspaceIdRef.current = currentWorkspaceId;

        setConversations([]);
        setCurrentConversationId(null);
        setMessages([]);
        setError(null);
        setRetryState({ isRetrying: false, pendingRetry: null, errorDetail: null, errorInfo: null });
    }, [currentWorkspaceId]);

    useEffect(() => {
        const initializeApp = async () => {
            await fetchConversations();
            setTimeout(() => {
                setConversations(prev => {
                    const hasTemporary = prev.some(conv => conv.isTemporary);
                    if (!hasTemporary) {
                        const newConversation = { id: uuidv4(), name: 'New Conversation', messages: [], isTemporary: true };
                        setCurrentConversationId(newConversation.id);
                        setMessages([]);
                        return [newConversation, ...prev];
                    }
                    return prev;
                });
            }, 100);
        };
        initializeApp();
    }, [fetchConversations]);

    const restoreSettingsFromConversation = useCallback((messages) => {
        for (let i = messages.length - 1; i >= 0; i--) {
            const message = messages[i];
            if (message.sender === 'bot' && message.model_slug && message.system_prompt_slug) {
                const modelValid = modelOptionsRef.current.some(opt => opt.value === message.model_slug);
                const promptValid = systemPromptOptionsRef.current.some(opt => opt.value === message.system_prompt_slug);
                if (modelValid) setModel(message.model_slug);
                if (promptValid) setSystemPrompt(message.system_prompt_slug);
                return true;
            }
        }
        return false;
    }, []);

    const handleConversationChange = useCallback(async (conversationId) => {
        setIsLoadingConversation(true);
        setError(null);
        setRetryState({ isRetrying: false, pendingRetry: null, errorDetail: null, errorInfo: null });

        try {
            const selectedConversation = conversationsRef.current.find(conv => conv.id === conversationId);
            if (!selectedConversation) throw new Error('Conversation not found');

            let conversationMessages;
            if (selectedConversation.isTemporary) {
                conversationMessages = selectedConversation.messages || [];
            } else {
                const response = await fetchWithAuth(`/api/conversations/${conversationId}`, {});
                if (!response.ok) throw new Error('Network response was not ok');
                const conversationData = await response.json();
                conversationMessages = conversationData.messages || [];
                setConversations(prev =>
                    prev.map(conv => conv.id === conversationId ? { ...conv, messages: conversationMessages } : conv)
                );
                restoreSettingsFromConversation(conversationMessages);
            }

            setMessages(conversationMessages);
            setCurrentConversationId(conversationId);
        } catch (error) {
            console.error('Error changing conversation:', error);
            setError('Failed to load conversation. Please try again.');
        } finally {
            setIsLoadingConversation(false);
            // Double rAF: first lets React commit and remount MessageDisplay,
            // second lets the browser lay out the new content.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    messageDisplayRef.current?.scrollToBottom('auto');
                });
            });
        }
    }, [restoreSettingsFromConversation]);

    // ── Chat submission ────────────────────────────────────────────────────────

    const handleSubmit = useCallback(async (e, messageText, isRetry = false) => {
        e.preventDefault();
        if (!messageText || !messageText.trim()) return;

        if (!model || !systemPrompt) {
            toast({ title: 'Not ready', description: 'Model and system prompt are still loading. Please wait.', status: 'warning', duration: 3000, isClosable: true });
            return;
        }

        const conversationId = currentConversationIdRef.current;
        // Captured at submit time (ai-chat#5): if the user switches workspace
        // mid-stream, the post-stream bookkeeping below must not touch the new
        // workspace's sidebar.
        const submitWorkspaceId = currentWorkspaceIdRef.current;
        const workspaceChanged = () => currentWorkspaceIdRef.current !== submitWorkspaceId;
        if (!isRetry) {
            setRetryState({ isRetrying: false, pendingRetry: null, errorDetail: null, errorInfo: null });
        }

        const currentModelInfo = modelOptions.find(opt => opt.value === model);
        const currentSystemPromptInfo = systemPromptOptions.find(opt => opt.value === systemPrompt);

        const userMessage = { id: uuidv4(), sender: 'user', text: messageText.trim() };
        if (!isRetry) setMessages(prev => [...prev, userMessage]);

        setError(null);
        setIsLoading(true);
        if (isRetry) setRetryState(prev => ({ ...prev, isRetrying: true }));

        try {
            const response = await fetchWithAuth('/api/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    prompt: messageText.trim(),
                    conversation_id: conversationId,
                    workspace_id: submitWorkspaceId,
                    model: model,
                    system_prompt: systemPrompt,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.log('Error response:', errorData);

                // The user already moved to another workspace: an error (and its
                // Retry button) from the old one must not surface in the new one.
                if (workspaceChanged()) return;

                if (errorData.detail?.error_type) {
                    const errorDetail = errorData.detail;
                    const errorInfo = getErrorDisplayInfo(errorDetail);

                    if (isRetryableError(errorDetail)) {
                        setRetryState({ isRetrying: false, pendingRetry: { prompt: messageText.trim(), model, systemPrompt, conversationId, workspaceId: submitWorkspaceId }, errorDetail, errorInfo });
                        return;
                    } else if (shouldShowModal(errorDetail)) {
                        setRetryState({ isRetrying: false, pendingRetry: null, errorDetail, errorInfo });
                        return;
                    } else {
                        setError(errorInfo.message);
                        return;
                    }
                }
                throw new Error('Network response was not ok.');
            }

            setRetryState({ isRetrying: false, pendingRetry: null, errorDetail: null, errorInfo: null });

            const botMessageId = uuidv4();
            const botMessage = {
                id: botMessageId,
                sender: 'bot',
                text: '',
                isStreaming: true,
                model_slug: model,
                model_display_name: currentModelInfo?.label || model,
                system_prompt_slug: systemPrompt,
                system_prompt_display_name: currentSystemPromptInfo?.label || systemPrompt,
            };
            setMessages(prev => [...prev, botMessage]);

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let lineBuffer = '';
            let displayText = '';
            let lastUpdate = 0;
            const UPDATE_INTERVAL = 50;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                const chunk = decoder.decode(value, { stream: true });
                lineBuffer += chunk;

                let newlineIndex;
                while ((newlineIndex = lineBuffer.indexOf('\n')) !== -1) {
                    const line = lineBuffer.slice(0, newlineIndex);
                    lineBuffer = lineBuffer.slice(newlineIndex + 1);
                    const trimmed = line.trim();

                    if (trimmed.startsWith('__STREAM_CONTROL__:')) {
                        const jsonPart = trimmed.slice('__STREAM_CONTROL__:'.length);
                        try {
                            const controlData = JSON.parse(jsonPart);
                            if (controlData.type === 'stream_error') {
                                const errorDetail = {
                                    error_type: controlData.error_type,
                                    message: controlData.message,
                                    provider: controlData.provider,
                                    retry_able: controlData.retry_able,
                                    category: controlData.retry_able ? 'retryable' : 'unknown',
                                };
                                const errorInfo = getErrorDisplayInfo(errorDetail);
                                setMessages(prev => prev.filter(msg => msg.id !== botMessageId));
                                if (workspaceChanged()) return; // see non-OK path above
                                if (isRetryableError(errorDetail)) {
                                    setRetryState({ isRetrying: false, pendingRetry: { prompt: messageText.trim(), model, systemPrompt, conversationId, workspaceId: submitWorkspaceId }, errorDetail, errorInfo });
                                } else {
                                    setError(errorInfo.message);
                                }
                                return;
                            }
                        } catch (e) {
                            console.error('Error parsing stream control:', e);
                        }
                        continue;
                    }

                    displayText += line + '\n';
                }

                const currentDisplayText = lineBuffer.startsWith('__STREAM_CONTROL__')
                    ? displayText
                    : displayText + lineBuffer;

                const now = Date.now();
                if (now - lastUpdate > UPDATE_INTERVAL) {
                    setMessages(prevMessages =>
                        prevMessages.map(msg =>
                            msg.id === botMessageId ? { ...msg, text: currentDisplayText } : msg
                        )
                    );
                    lastUpdate = now;
                    messageDisplayRef.current?.scrollIfAtBottom();
                }
            }

            displayText += lineBuffer;

            setMessages(prevMessages =>
                prevMessages.map(msg =>
                    msg.id === botMessageId
                        ? {
                            ...msg,
                            text: displayText,
                            isStreaming: false,
                            model_slug: model,
                            model_display_name: currentModelInfo?.label || model,
                            system_prompt_slug: systemPrompt,
                            system_prompt_display_name: currentSystemPromptInfo?.label || systemPrompt,
                        }
                        : msg
                )
            );
            messageDisplayRef.current?.scrollIfAtBottom();

            // Workspace switched mid-stream: the reply was saved server-side in
            // the original workspace; the view has already moved on. Skip the
            // sidebar merge and the delayed refresh (ai-chat#5).
            if (workspaceChanged()) return;

            const freshConversationId = currentConversationIdRef.current;
            const conversationResponse = await fetchWithAuth(`/api/conversations/${freshConversationId}`, {});

            if (conversationResponse.ok) {
                const updatedConversation = await conversationResponse.json();
                if (updatedConversation?.id) {
                    setConversations(prevConversations => {
                        const finalBotMessage = { ...botMessage, text: displayText, isStreaming: false };
                        const updated = prevConversations.map(conv =>
                            conv.id === freshConversationId
                                ? { ...conv, isTemporary: false, messages: [...(conv.messages || []), userMessage, finalBotMessage], name: updatedConversation.name || conv.name }
                                : conv
                        );
                        setTimeout(() => fetchConversations(), 500);
                        return updated;
                    });
                } else {
                    console.error('Received invalid conversation data:', updatedConversation);
                    setError('An error occurred while updating the conversation.');
                }
            } else {
                throw new Error('Failed to fetch updated conversation');
            }
        } catch (error) {
            console.error('Error:', error);
            if (!workspaceChanged()) {
                setError('An error occurred while processing your request.');
                setRetryState({ isRetrying: false, pendingRetry: null, errorDetail: null, errorInfo: null });
            }
        } finally {
            setIsLoading(false);
        }
        // The workspace is read from currentWorkspaceIdRef, NOT closed over: a
        // stale closure here would write the first message after a workspace
        // switch into the previous workspace (ai-chat#2). Keep it a ref.
    }, [model, systemPrompt, modelOptions, systemPromptOptions, fetchConversations, toast]);

    // ── Retry handlers ─────────────────────────────────────────────────────────

    const handleRetry = useCallback(async () => {
        if (!retryState.pendingRetry) return;
        // Defence in depth: never replay a prompt into a different workspace
        // than the one it was typed in.
        const { workspaceId } = retryState.pendingRetry;
        if (workspaceId != null && workspaceId !== currentWorkspaceIdRef.current) {
            setRetryState({ isRetrying: false, pendingRetry: null, errorDetail: null, errorInfo: null });
            return;
        }
        await handleSubmit({ preventDefault: () => {} }, retryState.pendingRetry.prompt, true);
    }, [retryState.pendingRetry, handleSubmit]);

    const handleRetryCancel = useCallback(() => {
        setRetryState({ isRetrying: false, pendingRetry: null, errorDetail: null, errorInfo: null });
    }, []);

    const handleErrorModalClose = useCallback(() => {
        setShowErrorModal(false);
        setRetryState({ isRetrying: false, pendingRetry: null, errorDetail: null, errorInfo: null });
    }, []);

    // ── Conversation CRUD handlers ─────────────────────────────────────────────

    const handleNewConversation = useCallback(() => {
        const newId = uuidv4();
        setConversations(prev => [{ id: newId, name: 'New Conversation', messages: [], isTemporary: true }, ...prev]);
        setCurrentConversationId(newId);
        setMessages([]);
        setError(null);
        setIsLoading(false);
        setRetryState({ isRetrying: false, pendingRetry: null, errorDetail: null, errorInfo: null });
    }, []);

    const handleSelectConversation = useCallback((id) => {
        handleConversationChange(id);
    }, [handleConversationChange]);

    const handleDeleteConversation = useCallback(async (id) => {
        try {
            const response = await fetchWithAuth(`/api/conversations/${id}`, { method: 'DELETE' });
            if (response.ok) {
                setConversations(prev => prev.filter(conv => conv.id !== id));
                if (id === currentConversationIdRef.current) handleNewConversation();
                toast({ title: 'Success', description: 'Conversation deleted successfully', status: 'success', duration: 3000, isClosable: true });
            } else {
                const errorData = await response.json();
                console.error('Deletion failed:', errorData);
                setError('Failed to delete conversation: ' + errorData.error);
            }
        } catch (error) {
            console.error('Error while deleting conversation:', error);
            setError('An error occurred while trying to delete the conversation.');
        }
    }, [handleNewConversation, toast]);

    const handleBulkDeleteConversations = useCallback(async (conversationIds) => {
        try {
            const isCurrentDeleted = conversationIds.includes(currentConversationIdRef.current);
            const deletedIds = await Promise.all(
                conversationIds.map(async (id) => {
                    const response = await fetchWithAuth(`/api/conversations/${id}`, { method: 'DELETE' });
                    if (!response.ok) throw new Error(`Failed to delete conversation ${id}`);
                    return id;
                })
            );
            setConversations(prev => prev.filter(conv => !deletedIds.includes(conv.id)));
            if (isCurrentDeleted) handleNewConversation();
            toast({ title: 'Success', description: `${deletedIds.length} conversation${deletedIds.length === 1 ? '' : 's'} deleted`, status: 'success', duration: 3000, isClosable: true });
        } catch (error) {
            console.error('Bulk delete failed:', error);
            setError('Failed to delete some conversations. Please try again.');
        }
    }, [handleNewConversation, toast]);

    const handleRenameConversation = useCallback(async (id, newName) => {
        try {
            const response = await fetchWithAuth(`/api/conversations/${id}/rename`, { method: 'PATCH', body: JSON.stringify({ name: newName }) });
            if (response.ok) {
                const data = await response.json();
                setConversations(prev => prev.map(conv => conv.id === id ? { ...conv, name: data.new_name } : conv));
                toast({ title: 'Success', description: 'Conversation renamed successfully', status: 'success', duration: 3000, isClosable: true });
            } else {
                const errorData = await response.json();
                console.error('Rename failed:', errorData);
                setError('Failed to rename conversation: ' + errorData.error);
            }
        } catch (error) {
            console.error('Error while renaming conversation:', error);
            setError('An error occurred while trying to rename the conversation.');
        }
    }, [toast]);

    const handleMoveConversation = useCallback(async (id, destinationWorkspaceId) => {
        // No-op guard on the client too: the server treats same-workspace as a
        // successful no-op, but there is no reason to make the round trip.
        if (destinationWorkspaceId == null || destinationWorkspaceId === currentWorkspaceId) return;

        const movedConversation = conversationsRef.current.find(conv => conv.id === id);
        const destination = workspaces.find(w => w.id === destinationWorkspaceId);

        try {
            const response = await fetchWithAuth(`/api/conversations/${id}/workspace`, {
                method: 'PATCH',
                body: JSON.stringify({ workspace_id: destinationWorkspaceId }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                console.error('Move failed:', errorData);
                setError(errorData.detail || 'Failed to move conversation.');
                return;
            }

            // The conversation now lives elsewhere, so it must leave THIS list --
            // the sidebar shows one workspace at a time (workspace_id is passed
            // to /api/conversations). Refetching would also work; removing it
            // locally avoids a flash of the stale row.
            setConversations(prev => prev.filter(conv => conv.id !== id));

            // If the user moved the conversation they were reading, they can no
            // longer see it here. Clear the view rather than leaving an orphaned
            // transcript on screen that belongs to another workspace.
            if (currentConversationIdRef.current === id) {
                handleNewConversation();
            }

            toast({
                title: 'Conversation moved',
                description: destination
                    ? `"${movedConversation?.name ?? 'Conversation'}" moved to ${destination.name}. Past token usage stays with the workspace that paid for it.`
                    : 'Conversation moved.',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error moving conversation:', error);
            setError('An error occurred while trying to move the conversation.');
        }
        // currentWorkspaceId is required, not lint noise: a stale value would
        // skip the no-op guard incorrectly. workspaces is needed for the name.
    }, [currentWorkspaceId, workspaces, handleNewConversation, toast]);

    const handleDuplicateConversation = useCallback(async (messageIndex) => {
        const currentConversation = conversationsRef.current.find(conv => conv.id === currentConversationIdRef.current);
        if (!currentConversation) return;
        const backendMessageIndex = Math.ceil((messageIndex + 1) / 2) - 1;

        try {
            const response = await fetchWithAuth('/api/conversations/copy', {
                method: 'POST',
                body: JSON.stringify({ conversation_id: currentConversationIdRef.current, workspace_id: currentWorkspaceId, message_index: backendMessageIndex, new_name: `Copy of: ${currentConversation.name}` }),
            });
            if (!response.ok) throw new Error('Failed to copy conversation');

            const result = await response.json();
            const transformedMessages = result.messages.flatMap(msg => [
                { id: uuidv4(), sender: 'user', text: msg.prompt },
                { id: uuidv4(), sender: 'bot', text: msg.response },
            ]);

            setConversations(prev => [{ id: result.new_conversation_id, name: result.name, messages: transformedMessages }, ...prev]);
            setCurrentConversationId(result.new_conversation_id);
            setMessages(transformedMessages);

            try {
                const fullResponse = await fetchWithAuth(`/api/conversations/${result.new_conversation_id}`, {});
                if (fullResponse.ok) {
                    const fullData = await fullResponse.json();
                    restoreSettingsFromConversation(fullData.messages || []);
                    setMessages(fullData.messages || []);
                }
            } catch (err) {
                console.error('Error fetching full conversation data after duplication:', err);
            }

            toast({ title: 'Success', description: 'Conversation duplicated successfully', status: 'success', duration: 3000, isClosable: true });
        } catch (error) {
            console.error('Error copying conversation:', error);
            setError('Failed to copy conversation. Please try again.');
        }
        // See the note on handleSendMessage: a stale currentWorkspaceId here
        // would copy the conversation into the previously-selected workspace.
    }, [restoreSettingsFromConversation, toast, currentWorkspaceId]);

    // ── View navigation ────────────────────────────────────────────────────────

    const handleViewChange = useCallback((view) => {
        setCurrentView(view);
        if (view === 'chat') window.location.hash = '';
        else window.location.hash = view;
    }, []);

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <Flex h="100vh" bg="chat.bg" overflow="hidden" direction="column">
            <TopNav
                currentView={currentView}
                onViewChange={handleViewChange}
                user={user}
                onChangePassword={() => setShowChangePassword(true)}
                onLogout={logout}
                adminWithoutWorkspace={adminWithoutWorkspace}
            />

            {/* State the rule on its face rather than leaving the admin to
                wonder why chat vanished -- the same principle as the
                "Metadata only" notices in WorkspaceManagement (!31). */}
            {adminWithoutWorkspace && (
                <Box
                    bg="orange.50"
                    borderBottomWidth="1px"
                    borderColor="orange.200"
                    color="orange.900"
                    px={4}
                    py={2}
                    fontSize="sm"
                >
                    {workspaceError
                        ? `Your workspaces could not be loaded (${workspaceError}). Administration is still available below.`
                        : 'You are not a member of any workspace, so chat and documents are unavailable. As an administrator you can grant yourself membership below \u2014 the grant is recorded and visible to anyone who looks.'}
                </Box>
            )}

            <Flex flex="1" overflow="hidden">
                {currentView === 'users' ? (
                    <Box flex="1" overflow="auto"><UserManagement /></Box>
                ) : currentView === 'workspaces' ? (
                    <Box flex="1" overflow="auto"><WorkspaceManagement /></Box>
                ) : currentView === 'credentials' ? (
                    <Box flex="1" overflow="auto"><CredentialManagement /></Box>
                ) : adminWithoutWorkspace ? (
                    // Belt and braces: the hash router already redirects a
                    // workspace-less admin away from chat/documents, but a
                    // future view added to the switch must not silently mount
                    // a component that needs a workspace.
                    <Box flex="1" overflow="auto"><WorkspaceManagement /></Box>
                ) : currentView === 'documents' ? (
                    <Box flex="1" overflow="auto"><DocumentLibrary /></Box>
                ) : (
                    <ChatView
                        conversations={conversations}
                        currentConversationId={currentConversationId}
                        onSelectConversation={handleSelectConversation}
                        onNewConversation={handleNewConversation}
                        onDeleteConversation={handleDeleteConversation}
                        onRenameConversation={handleRenameConversation}
                        onMoveConversation={handleMoveConversation}
                        workspaces={workspaces}
                        currentWorkspaceId={currentWorkspaceId}
                        onBulkDeleteConversations={handleBulkDeleteConversations}
                        messageDisplayRef={messageDisplayRef}
                        messages={messages}
                        error={error}
                        isLoading={isLoading}
                        isLoadingConversation={isLoadingConversation}
                        currentModelDisplayName={currentModelDisplayName}
                        onDuplicateConversation={handleDuplicateConversation}
                        retryState={retryState}
                        onRetry={handleRetry}
                        onRetryCancel={handleRetryCancel}
                        onSubmit={handleSubmit}
                        model={model}
                        setModel={setModel}
                        systemPrompt={systemPrompt}
                        setSystemPrompt={setSystemPrompt}
                    />
                )}
            </Flex>

            <ErrorModal
                isOpen={showErrorModal}
                onClose={handleErrorModalClose}
                errorInfo={retryState.errorInfo}
            />

            <ChangePasswordModal
                isOpen={showChangePassword}
                onClose={() => setShowChangePassword(false)}
            />
        </Flex>
    );
}

// ─── Auth wrapper ──────────────────────────────────────────────────────────────

function AuthWrapper() {
    const { user, loading } = useAuth();
    if (loading) return <Center h="100vh" bg="gray.50"><Spinner size="xl" color="brand.500" thickness="4px" /></Center>;
    if (!user) return <Login />;
    return <WorkspaceGate />;
}

// Every write names a workspace, so the app cannot function before we know
// which workspaces the user belongs to. Waiting here keeps that invariant in
// ONE place instead of forcing every call site to handle a null workspace.
//
// THE ADMIN EXCEPTION (2026-08-07) -- why this gate is not absolute
// -----------------------------------------------------------------
// A platform superuser holding NO membership is not a broken state, it is the
// DESIGNED state: `is_admin` is a grant-path, not a read-path, and
// create_workspace deliberately grants its creator nothing. So a fresh admin,
// or one who revoked their own last membership, legitimately has zero
// workspaces -- and the /api/admin routes they need require only
// get_current_admin, never a membership.
//
// Gating those routes behind a membership check they do not use made the
// instance BRICKABLE: the admin UI that issues the grant sat behind the very
// wall only a grant could remove, with no second admin to call. Steph hit this
// on a live instance -- one admin, one user, removed himself from every
// workspace, and nothing on the box could let him back in.
//
// The fix is scoped deliberately: an admin gets the ADMIN surface, not chat.
// The boundary is untouched -- no content, no conversations, no documents are
// reachable without a visible Membership row. Only the ability to grant one.
function WorkspaceGate() {
    const { user } = useAuth();
    const { loading, error, workspaces, currentWorkspaceId } = useWorkspace();

    if (loading) {
        return <Center h="100vh" bg="gray.50"><Spinner size="xl" color="brand.500" thickness="4px" /></Center>;
    }

    if (error) {
        // An admin must reach the grant path even when /api/workspaces fails,
        // for the same reason as below: recovery cannot depend on the surface
        // that is broken. Members still see the error -- there is nothing they
        // could do with the app anyway.
        if (user?.is_admin) {
            return <AppContent adminWithoutWorkspace workspaceError={error} />;
        }
        return (
            <Center h="100vh" bg="gray.50">
                <Box textAlign="center" px={6}>
                    <Box fontSize="lg" fontWeight="semibold" mb={2}>Could not load your workspaces</Box>
                    <Box color="gray.600">{error}</Box>
                </Box>
            </Center>
        );
    }

    const hasWorkspace = workspaces.length > 0 && currentWorkspaceId != null;

    // An admin without a workspace gets the admin surface so they can grant
    // themselves (or anyone) access. Chat stays unavailable -- every write
    // names a workspace, and there is none to name.
    if (!hasWorkspace && user?.is_admin) {
        return <AppContent adminWithoutWorkspace />;
    }

    // A non-admin with no membership cannot read or write anything, and has no
    // route to fix it themselves. Say so plainly rather than showing an empty
    // chat that fails on the first message.
    if (!hasWorkspace) {
        return (
            <Center h="100vh" bg="gray.50">
                <Box textAlign="center" px={6} maxW="md">
                    <Box fontSize="lg" fontWeight="semibold" mb={2}>No workspace yet</Box>
                    <Box color="gray.600">
                        Your account is not a member of any workspace. Ask an
                        administrator to add you to one.
                    </Box>
                </Box>
            </Center>
        );
    }

    return <AppContent />;
}

function App() {
    return (
        <AuthProvider>
            <WorkspaceProvider>
                <AuthWrapper />
            </WorkspaceProvider>
        </AuthProvider>
    );
}

export default App;